import { Button } from '@artstorefronts/ui';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGoBack } from '~/hooks/useGoBack';

import AdminLayout from '../../../components/AdminLayout';
import SparkForm from '../../../components/SparkForm';

const QUERY = gql`
  query CompletionType($id: ID!) {
    completionType(id: $id) {
      id
      permissionKey
      name
      icon
      order
      promptData
      description
      planNames
      userRules {
        id
        active
        rules
      }
      enabled
      expiresAt
      updatedAt
      model
      additionalInfo
      signupHeaderText
      signupFormLabelText
      signupButtonText
      linkOutUrl
      isLinkOutNewWindow
      isLinkOut
    }
    models {
      id
      provider
      name
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation UpdateCompletionType($input: UpdateCompletionTypeInput!) {
    updateCompletionType(input: $input) {
      id
      permissionKey
      name
      icon
      order
      promptData
      description
      planNames
      enabled
      expiresAt
      additionalInfo
      signupHeaderText
      signupFormLabelText
      signupButtonText
      linkOutUrl
      isLinkOutNewWindow
      isLinkOut
    }
  }
`;

function DataWrapper() {
  const { id } = useParams();
  const { data, loading } = useQuery(QUERY, {
    variables: { id },
  });

  const type = data?.completionType;

  if (loading || !type) {
    return null;
  }

  return (
    <>
      <AdminSparkEdit type={type} models={data?.models} />
    </>
  );
}

function AdminSparkEdit({ type, models }: any) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [updateSpark, { loading }] = useMutation(UPDATE_MUTATION);

  const goBack = useGoBack();

  async function handleSubmit(data: any) {
    try {
      await updateSpark({
        variables: {
          input: {
            id: type.id,
            ...data,
          },
        },
      });
      toast.success(t('sparks.edit.toast-success'));
    } catch (error) {
      console.error('Failed to update spark:', error);
      toast.error(t('sparks.edit.toast-error'));
    }
  }

  const lastUpdated = type.updatedAt ? new Date(type.updatedAt).toLocaleString() : t('sparks.edit.never-updated');

  return (
    <AdminLayout
      title={t('sparks.edit.page-title', { name: type.name })}
      description={t('sparks.edit.page-description', { date: lastUpdated })}
      action={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => goBack({ fallback: '/admin/sparks' })}>
            {tc('cancel')}
          </Button>
          <Button type="submit" form="spark-form" size="sm" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {tc('saving')}
              </>
            ) : (
              tc('save-changes')
            )}
          </Button>
        </div>
      }
    >
      <div className="w-full">
        <SparkForm id="spark-form" type={type} models={models} onSubmit={handleSubmit} />
      </div>
    </AdminLayout>
  );
}

export default DataWrapper;
